import React from "react";
import { useHistory } from "react-router-dom";

/**
 * CandidateMatchAlertCard — "X new jobs match your profile above 85%"
 *
 * Personalized card (visibility=personal). Only the matched user sees it.
 * View Matches CTA navigates to the job match score page.
 *
 * Props:
 *   card — feed card object with metadata containing:
 *     match_count, match_threshold, top_match_score
 */
const CandidateMatchAlertCard = ({ card }) => {
  const history = useHistory();
  const meta = card.metadata || {};

  const handleViewMatches = () => {
    if (card.cta_target_url) {
      history.push(card.cta_target_url);
    } else {
      history.push("/job-match-score");
    }
  };

  return (
    <div style={styles.card}>
      <div style={styles.cardBody}>
        <div style={styles.headerRow}>
          <div style={styles.iconWrap}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
              <path d="M9 11l3 3L22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
          </div>
          <span style={styles.timestamp}>{formatTime(card.created_at)}</span>
        </div>

        <h6 style={styles.title}>{card.title}</h6>
        <p style={styles.detail}>{card.preview_text}</p>

        {meta.top_match_score && (
          <div style={styles.scoreRow}>
            <span style={styles.scoreLabel}>Top match</span>
            <span style={styles.scoreValue}>{meta.top_match_score}%</span>
          </div>
        )}

        <button style={styles.matchBtn} onClick={handleViewMatches}>
          View Matches
        </button>
      </div>
    </div>
  );
};

function formatTime(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffHrs = (now - date) / (1000 * 60 * 60);
  if (diffHrs < 1) return "Just now";
  if (diffHrs < 24) return `${Math.floor(diffHrs)}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays === 1) return "1d ago";
  return `${diffDays}d ago`;
}

const styles = {
  card: {
    backgroundColor: "#3a6ed4",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(58,110,212,0.25)",
    marginBottom: "16px",
  },
  cardBody: {
    padding: "20px",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
  },
  iconWrap: {
    width: "40px",
    height: "40px",
    borderRadius: "20px",
    backgroundColor: "rgba(255,255,255,0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  timestamp: {
    fontSize: "12px",
    color: "rgba(255,255,255,0.7)",
  },
  title: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#fff",
    margin: "0 0 6px 0",
  },
  detail: {
    fontSize: "14px",
    color: "rgba(255,255,255,0.85)",
    margin: "0 0 16px 0",
    lineHeight: 1.5,
  },
  scoreRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "16px",
  },
  scoreLabel: {
    fontSize: "13px",
    color: "rgba(255,255,255,0.7)",
  },
  scoreValue: {
    fontSize: "18px",
    fontWeight: "800",
    color: "#fff",
  },
  matchBtn: {
    padding: "10px 24px",
    backgroundColor: "#fff",
    color: "#3a6ed4",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
};

export default CandidateMatchAlertCard;
